import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { RESULT_META } from "@/components/ResultBadge";
import { formatDate } from "@/lib/date";

/**
 * WeeklyStrip — 7 cells for the last 7 days.
 * Each cell has a colored dot (or muted circle if no entry) and the weekday
 * abbreviation below. Tap → shows a small info popover for that day.
 */
export function WeeklyStrip({ days }) {
  const { language, t } = useI18n();
  const [active, setActive] = useState(null); // date string or null

  const activeDay = active
    ? days.find((d) => d.date === active) || null
    : null;

  return (
    <div className="w-full" data-testid="weekly-strip">
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const entry = day.entry;
          const isActive = active === day.date;
          const color = entry
            ? (RESULT_META[entry.result] || RESULT_META.stay).color
            : "rgba(255,255,255,0.06)";
          return (
            <button
              type="button"
              key={day.date}
              onClick={() =>
                setActive((prev) => (prev === day.date ? null : day.date))
              }
              data-testid={`weekly-cell-${day.date}`}
              aria-label={day.date}
              className={`group flex flex-col items-center gap-1.5 pt-2 pb-1 rounded-xl transition-colors duration-200 ${
                isActive ? "bg-white/[0.05]" : "hover:bg-white/[0.03]"
              }`}
            >
              <span
                className="block w-7 h-7 rounded-full transition-transform duration-200 group-hover:scale-105"
                style={{
                  background: entry
                    ? `radial-gradient(circle, ${color}55 0%, ${color}22 60%, ${color}00 80%)`
                    : "transparent",
                  border: entry ? `1.5px solid ${color}` : "1.5px dashed rgba(255,255,255,0.14)",
                }}
              />
              <span
                className={`text-[10px] uppercase ${
                  isActive ? "text-[#F4C95D]" : "text-white/55"
                }`}
                style={{ letterSpacing: "0.06em" }}
              >
                {shortWeekday(day.date, language)}
              </span>
            </button>
          );
        })}
      </div>

      {activeDay ? (
        <div
          className="mt-3 flex items-center gap-2 text-[13px] px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] capitalize"
          data-testid="weekly-strip-detail"
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: activeDay.entry
                ? (RESULT_META[activeDay.entry.result] || RESULT_META.stay)
                    .color
                : "rgba(255,255,255,0.2)",
            }}
          />
          <span className="text-white/85 flex-1">
            {formatDate(activeDay.date, language)}
          </span>
          <span className="text-white/60 text-xs">
            {activeDay.entry
              ? t(`result.labels.${activeDay.entry.result}.title`)
              : t("dashboard.strip.empty")}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Very compact weekday label, e.g. Pn / Mo. */
function shortWeekday(iso, language) {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const locale = language === "pl" ? "pl-PL" : "en-US";
  try {
    // "narrow" = 1-letter, "short" = 3-letter. We use short for readability.
    const w = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);
    // PL "pon." → "Pon", strip trailing dot, capitalize first
    return w.replace(/\.$/, "").slice(0, 2).replace(/^./, (c) => c.toUpperCase());
  } catch {
    return "";
  }
}
